"use client"
import { useRouter, useSearchParams } from 'next/navigation';
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@quintoandar-tokko/jopijs";
import { setCalendarData } from "@/store/slices/components/calendar/CalendarSlice";
import { confirmVisit } from '@/store/slices/components/calendar/services';
import CalendarButton from "../calendarButton/CalendarButton";
import CalendarService from "@/services/CalendarService";
import Trans from "@/components/shared/Trans";
import translations from './calendarControls.trans.json'
import style from "./calendarControls.module.scss"

const FIRST_STEP = 1
const LAST_STEP = 2

export default function CalendarControls() {
  const router = useRouter();
  const searchParams = useSearchParams()
  const contact_id = searchParams.get('contact_id')
  const calendar = useSelector(state => state.CalendarSlice)
  const dispatch = useDispatch()
  const { form } = calendar

  const isSomeInputWithError = Object.values(form).some(el => el.error)
  const isSomeRequiredInputEmpty = Object.values(form).some(el => el.needed && el.required && !el.value)
  const isNextButtonDisabled = !calendar.visit.date || !calendar.visit.hour
    || (calendar.step === LAST_STEP && (isSomeInputWithError || isSomeRequiredInputEmpty))
  const isCancelButtonDisabled = calendar.step === FIRST_STEP && typeof window !== 'undefined' && window.history.length <= 1

  const handleClickBack = () => {
    if (calendar.step === FIRST_STEP) {
      if (typeof window === 'undefined') return

      if (router?.back) {
        router.back();
      }
    }

    dispatch(setCalendarData({ key: 'step', value: calendar.step - 1 }))
  }

  const getIsCalendarStillOpen = async () => {
    try {
      dispatch(setCalendarData({ key: 'visit', subkey:'confirming', value: true }))

      const { host } = calendar
      const openStatus = 1
      const propertyId = calendar.property.id
      const dateFrom = new Date().toISOString().split('T')[0]
      const dateSelected = dateFrom
      const result = await CalendarService.getPropertySchedule({ propertyId, dateFrom, dateSelected, host })

      return result.status === openStatus
    } catch (error) {
      console.log(error)
    }
  }

  const handleClickNext = async () => {
    if (calendar.step !== LAST_STEP) {
      return dispatch(setCalendarData({ key: 'step', value: calendar.step + 1 }))
    }
    const { host } = calendar
    const isCalendarStillOpen = await getIsCalendarStillOpen()

    if (!isCalendarStillOpen) {
      return dispatch(setCalendarData({ key: 'isOpen', value: false }))
    }

    const fixedFields = {
      name_surname: form.fullname,
      phone: form.phone,
      email: form.email,
      comment: form.comment,
    }

    const payload = {
      host,
      property_id: calendar.property.id,
      date: new Date(calendar.visit.date).toISOString().split('T')[0],
      time: calendar.visit.hour,
      time_zone: calendar.property.gmt,
    }

    if (contact_id) {
      payload.contact_id = contact_id
    }

    const subPayload = []

    for (const [key, values] of Object.entries(fixedFields)) {
      if (values.needed && values.value) {
        payload[key] = values.value
      }
    }

    for (const [key, values] of Object.entries(form)) {
      if (values.isCustom && values.needed && values.value) {
        subPayload.push({
          extra_comment_value: values.value,
          extra_comment_name: key
        })
      }
    }

    if (subPayload.length > 0) {
      payload.extra_comments = subPayload
    }

    dispatch(confirmVisit(payload))
      .then(result => {
        const { payload } = result
        const { id } = payload
        let url = `/visit/${id}`

        if (contact_id) {
          url += `?contact_id=${contact_id}`
        }
        router.push(url)
      })
      .catch(error => console.log(error))
  }

  return (
    <Box as="footer" className={style.calendarControl}>
      <CalendarButton
        variant='subtle'
        disabled={isCancelButtonDisabled}
        onClick={handleClickBack}
      >
        {Trans(translations, 'CalendarControls.Cancel')}
      </CalendarButton>

      <CalendarButton
        variant='primary'
        onClick={handleClickNext}
        disabled={isNextButtonDisabled}
        loading={calendar.visit.confirming}
      >
        {calendar.step < LAST_STEP
          ? Trans(translations, 'CalendarControls.Next')
          : Trans(translations, 'CalendarControls.SendRequest')
        }
      </CalendarButton>
    </Box>
  );
}
